import React, { useState, useMemo } from 'react';
import { Search, ChevronDown, ChevronRight, Building2 } from 'lucide-react';
import { getStatusStyle } from '../../config/statusColors';
import { calcMarketValue, daysBetween, formatCurrency } from '../../lib/fixedIncome';
import { ENTITIES } from '../../data/portfolioMockData';
import ExportToolbar from './ExportToolbar';
import { exportToExcel, exportToPdf, HOLDINGS_EXPORT_COLUMNS } from '../../lib/exportUtils';

const HoldingsTab = ({ positions, role }) => {
  const [sortField, setSortField] = useState('country');
  const [sortDir, setSortDir] = useState('asc');
  const [filters, setFilters] = useState({
    country: '',
    instrument: '',
    status: '',
    search: '',
  });
  const [expandedRow, setExpandedRow] = useState(null);
  const [selectedEntity, setSelectedEntity] = useState('all');

  const entities = Object.values(ENTITIES);

  const uniqueCountries = useMemo(
    () => [...new Set(positions.map((p) => p.country))].sort(),
    [positions]
  );

  const uniqueInstruments = useMemo(
    () => [...new Set(positions.map((p) => p.instrument))].sort(),
    [positions]
  );

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const filteredPositions = useMemo(() => {
    let result = [...positions];

    if (role === 'investor_admin' && selectedEntity !== 'all') {
      result = result.filter((p) => p.entityId === selectedEntity);
    }

    if (filters.country) {
      result = result.filter((p) => p.country === filters.country);
    }

    if (filters.instrument) {
      result = result.filter((p) => p.instrument === filters.instrument);
    }

    if (filters.status) {
      result = result.filter(
        (p) => p.status.toLowerCase() === filters.status.toLowerCase()
      );
    }

    if (filters.search) {
      const term = filters.search.toLowerCase();
      result = result.filter(
        (p) =>
          (p.id && p.id.toLowerCase().includes(term)) ||
          (p.isin && p.isin.toLowerCase().includes(term)) ||
          (p.country && p.country.toLowerCase().includes(term)) ||
          (p.custodianBank && p.custodianBank.toLowerCase().includes(term))
      );
    }

    return result;
  }, [positions, role, selectedEntity, filters]);

  const sortedPositions = useMemo(() => {
    const sorted = [...filteredPositions];

    sorted.sort((a, b) => {
      let aVal, bVal;

      switch (sortField) {
        case 'country':
          aVal = (a.country || '').toLowerCase();
          bVal = (b.country || '').toLowerCase();
          break;
        case 'instrument':
          aVal = (a.instrument || '').toLowerCase();
          bVal = (b.instrument || '').toLowerCase();
          break;
        case 'faceValue':
          aVal = a.faceValue || 0;
          bVal = b.faceValue || 0;
          break;
        case 'yieldToMaturity':
          aVal = a.yieldToMaturity || 0;
          bVal = b.yieldToMaturity || 0;
          break;
        case 'maturityDate':
          aVal = new Date(a.maturityDate).getTime();
          bVal = new Date(b.maturityDate).getTime();
          break;
        case 'status':
          aVal = (a.status || '').toLowerCase();
          bVal = (b.status || '').toLowerCase();
          break;
        default:
          aVal = a[sortField] || '';
          bVal = b[sortField] || '';
      }

      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredPositions, sortField, sortDir]);

  const { totalFace, totalMarket } = useMemo(() => {
    let totalFace = 0;
    let totalMarket = 0;

    filteredPositions.forEach((p) => {
      totalFace += p.faceValue || 0;
      totalMarket += calcMarketValue(p.faceValue, p.couponRate || 0, p.yieldToMaturity || 0, daysBetween(new Date(), new Date(p.maturityDate)));
    });

    return { totalFace, totalMarket };
  }, [filteredPositions]);

  const renderSortArrow = (field) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  const dropdownClass =
    'bg-white border border-zinc-300 rounded-none px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#4D4D4D]';

  const handleExportExcel = () => {
    exportToExcel(sortedPositions, HOLDINGS_EXPORT_COLUMNS, 'Holdings', 'portfolio-holdings');
  };

  return (
    <div className="space-y-4">
      {/* Export Toolbar */}
      <div className="flex justify-end">
        <ExportToolbar onExportExcel={handleExportExcel} onExportPdf={exportToPdf} />
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-none border border-zinc-200 p-4">
        <div className="flex flex-wrap items-center gap-3">
          {role === 'investor_admin' && (
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-zinc-500" />
              <select
                value={selectedEntity}
                onChange={(e) => setSelectedEntity(e.target.value)}
                className={dropdownClass}
              >
                <option value="all">All Entities</option>
                {entities.map((entity) => (
                  <option key={entity.id} value={entity.id}>
                    {entity.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <select
            value={filters.country}
            onChange={(e) => handleFilterChange('country', e.target.value)}
            className={dropdownClass}
          >
            <option value="">All Countries</option>
            {uniqueCountries.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>

          <select
            value={filters.instrument}
            onChange={(e) => handleFilterChange('instrument', e.target.value)}
            className={dropdownClass}
          >
            <option value="">All Instruments</option>
            {uniqueInstruments.map((i) => (
              <option key={i} value={i}>
                {i}
              </option>
            ))}
          </select>

          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className={dropdownClass}
          >
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="maturing">Maturing</option>
            <option value="matured">Matured</option>
          </select>

          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              type="text"
              placeholder="Search ID, ISIN, country, custodian..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className={`${dropdownClass} w-full pl-9`}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-none shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-3 py-3 text-left font-mono text-xs font-semibold text-gray-600 uppercase tracking-wider w-8">
                  {/* expand icon column */}
                </th>
                <th className="px-3 py-3 text-left font-mono text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  ID
                </th>
                <th
                  className="px-3 py-3 text-left font-mono text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-gray-900"
                  onClick={() => handleSort('country')}
                >
                  Country{renderSortArrow('country')}
                </th>
                <th
                  className="px-3 py-3 text-left font-mono text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-gray-900"
                  onClick={() => handleSort('instrument')}
                >
                  Instrument{renderSortArrow('instrument')}
                </th>
                <th className="px-3 py-3 text-left font-mono text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  ISIN
                </th>
                <th
                  className="px-3 py-3 text-right font-mono text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-gray-900"
                  onClick={() => handleSort('faceValue')}
                >
                  Face Value{renderSortArrow('faceValue')}
                </th>
                <th className="px-3 py-3 text-right font-mono text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Market Value
                </th>
                <th
                  className="px-3 py-3 text-right font-mono text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-gray-900"
                  onClick={() => handleSort('yieldToMaturity')}
                >
                  YTM (%){renderSortArrow('yieldToMaturity')}
                </th>
                <th className="px-3 py-3 text-left font-mono text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Purchase Date
                </th>
                <th
                  className="px-3 py-3 text-left font-mono text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-gray-900"
                  onClick={() => handleSort('maturityDate')}
                >
                  Maturity Date{renderSortArrow('maturityDate')}
                </th>
                <th className="px-3 py-3 text-right font-mono text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Days Left
                </th>
                <th className="px-3 py-3 text-center font-mono text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Foreign Exchange Rate (Purchase → Current)
                </th>
                <th
                  className="px-3 py-3 text-center font-mono text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-gray-900"
                  onClick={() => handleSort('status')}
                >
                  Status{renderSortArrow('status')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedPositions.map((p) => {
                const marketValue = calcMarketValue(p.faceValue, p.couponRate || 0, p.yieldToMaturity || 0, daysBetween(new Date(), new Date(p.maturityDate)));
                const daysLeft = daysBetween(
                  new Date(),
                  new Date(p.maturityDate)
                );
                const isExpanded = expandedRow === p.id;
                const statusStyle = getStatusStyle(p.status);

                return (
                  <React.Fragment key={p.id}>
                    <tr
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() =>
                        setExpandedRow(isExpanded ? null : p.id)
                      }
                    >
                      <td className="px-3 py-3 text-gray-500">
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </td>
                      <td className="px-3 py-3 font-mono text-xs text-gray-700">
                        {p.id}
                      </td>
                      <td className="px-3 py-3 font-mono text-sm text-gray-900">
                        {p.country}
                      </td>
                      <td className="px-3 py-3 font-mono text-sm text-gray-900">
                        {p.instrument}
                      </td>
                      <td className="px-3 py-3 font-mono text-xs text-gray-600">
                        {p.isin}
                      </td>
                      <td className="px-3 py-3 text-right font-mono text-sm text-gray-900">
                        {formatCurrency(p.faceValue)}
                      </td>
                      <td className="px-3 py-3 text-right font-mono text-sm text-gray-900">
                        {formatCurrency(marketValue)}
                      </td>
                      <td className="px-3 py-3 text-right font-mono text-sm text-gray-900">
                        {(p.yieldToMaturity * 100).toFixed(2)}%
                      </td>
                      <td className="px-3 py-3 font-mono text-sm text-gray-700">
                        {p.purchaseDate}
                      </td>
                      <td className="px-3 py-3 font-mono text-sm text-gray-700">
                        {p.maturityDate}
                      </td>
                      <td
                        className={`px-3 py-3 text-right font-mono text-sm ${
                          daysLeft < 91
                            ? 'text-amber-600 font-semibold'
                            : 'text-gray-900'
                        }`}
                      >
                        {daysLeft}
                      </td>
                      <td className="px-3 py-3 text-center font-mono text-sm text-gray-700">
                        {p.fxRateAtPurchase.toFixed(2)} →{' '}
                        {p.currentFxRate.toFixed(2)}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span
                          className={`inline-block px-2 py-1 text-xs font-mono font-semibold rounded ${statusStyle}`}
                        >
                          {p.status}
                        </span>
                      </td>
                    </tr>

                    {/* Expanded Detail Panel */}
                    {isExpanded && (
                      <tr className="bg-zinc-50 border-t border-zinc-200">
                        <td colSpan={13} className="px-6 py-4">
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                            <div>
                              <span className="block font-mono text-xs text-zinc-500 uppercase tracking-wider mb-1">
                                Custodian Bank
                              </span>
                              <span className="font-mono text-sm text-gray-900">
                                {p.custodianBank}
                              </span>
                            </div>
                            <div>
                              <span className="block font-mono text-xs text-zinc-500 uppercase tracking-wider mb-1">
                                Auction Reference
                              </span>
                              <span className="font-mono text-sm text-gray-900">
                                {p.auctionReference || '—'}
                              </span>
                            </div>
                            <div>
                              <span className="block font-mono text-xs text-zinc-500 uppercase tracking-wider mb-1">
                                Purchase Price
                              </span>
                              <span className="font-mono text-sm text-gray-900">
                                {p.purchasePrice
                                  ? formatCurrency(p.purchasePrice)
                                  : '—'}
                              </span>
                            </div>
                            <div>
                              <span className="block font-mono text-xs text-zinc-500 uppercase tracking-wider mb-1">
                                ISIN
                              </span>
                              <span className="font-mono text-sm text-gray-900">
                                {p.isin}
                              </span>
                            </div>
                            <div>
                              <span className="block font-mono text-xs text-zinc-500 uppercase tracking-wider mb-1">
                                Currency
                              </span>
                              <span className="font-mono text-sm text-gray-900">
                                {p.currency || '—'}
                              </span>
                            </div>
                            <div>
                              <span className="block font-mono text-xs text-zinc-500 uppercase tracking-wider mb-1">
                                Accrued Interest
                              </span>
                              <span className="font-mono text-sm text-gray-900">
                                {p.accruedInterest
                                  ? formatCurrency(p.accruedInterest)
                                  : formatCurrency(
                                      p.faceValue *
                                        p.yieldToMaturity *
                                        (daysBetween(
                                          new Date(p.purchaseDate),
                                          new Date()
                                        ) /
                                          365)
                                    )}
                              </span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}

              {/* Summary Footer Row */}
              <tr className="bg-gray-50 font-semibold border-t border-gray-200">
                <td className="px-3 py-3" />
                <td
                  className="px-3 py-3 font-mono text-sm text-gray-900"
                  colSpan={4}
                >
                  Total ({filteredPositions.length} positions)
                </td>
                <td className="px-3 py-3 text-right font-mono text-sm text-gray-900">
                  {formatCurrency(totalFace)}
                </td>
                <td className="px-3 py-3 text-right font-mono text-sm text-gray-900">
                  {formatCurrency(totalMarket)}
                </td>
                <td className="px-3 py-3" />
                <td className="px-3 py-3" />
                <td className="px-3 py-3" />
                <td className="px-3 py-3" />
                <td className="px-3 py-3" />
                <td className="px-3 py-3" />
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default HoldingsTab;
